import { useCallback, useRef, useEffect, useState, useMemo } from 'react'
import { connect } from 'react-redux'
import { PageArea, PageProps } from '..'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { isDeletableTicketTypeRow, TicketTypeRow } from './TicketTypeRow'
import { useTicketTypesGridOptions } from './TicketTypeOptions'
import TicketTypesHeader from './components/Header'
import SavePopper from '../../containers/BulkSheetView/components/header/SaveButtonArea'
import { requireSave, doNotRequireSave } from '../../../store/requiredSaveData'
import {
  RowDataUpdatedEvent,
  GetContextMenuItemsParams,
  RowDragEndEvent,
  SortChangedEvent,
  FilterChangedEvent,
} from 'ag-grid-community'
import Loading from '../../components/process-state-notifications/Loading'
import { getSelectedNode } from '../../containers/BulkSheetView/lib/gridApi'
import { checkForChanges } from './lib/dataConverters'
import { createContextMenuItems, getRowStyle } from './lib/gridUtils'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'
import { useTicketTypeData } from './hooks/useTicketTypeData'
import { useTicketTypeFilters } from './hooks/useTicketTypeFilters'
import { useDragStyle } from '../../containers/BulkSheetView/hooks/gridEvents/rowDrag'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import CancelConfirmDialog from '../../components/dialogs/CancelConfirmDialog'
import { createNewTicketTypeRow } from './ticketTypeHelpers'
import AddRowCountInputDialog, {
  AddRowCountInputDialogState,
} from '../../containers/BulkSheet/AddRowCountInputDialog'
import { intl } from '../../../i18n'
import { DeleteRowConfirmationDialog } from '../../containers/BulkSheetView/components/dialog/DeleteRowConfirmationDialog'
type Props = PageProps & {
  dispatch: any
}

const TicketTypes = projectPrivate((props: Props) => {
  const { uuid: functionUuid, dispatch } = props
  const gridOptionsFromHook = useTicketTypesGridOptions()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const gridOptions = useMemo(() => gridOptionsFromHook, [])
  const ref = useRef<HTMLDivElement>(null)
  const gridApiRef = useRef<any>(null)

  // キャンセル確認ダイアログの状態管理
  const [showCancelDialog, setShowCancelDialog] = useState(false)

  // 追加行数入力ダイアログの状態管理
  const [addRowCountDialogState, setAddRowCountDialogState] =
    useState<AddRowCountInputDialogState>({
      open: false,
    })

  // 削除確認ダイアログの状態管理
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false)
  const [deleteTargetNames, setDeleteTargetNames] = useState<string[]>([])

  // カスタムフック: データ管理
  const {
    rowData,
    originalData,
    hasUnsavedChanges,
    loading,
    error,
    handleReload,
    handleSubmit,
    handleDataChange,
    clearError,
  } = useTicketTypeData({
    functionUuid,
    gridOptions,
  })

  // カスタムフック: フィルター・ソート管理
  const {
    filteredColumns,
    sortColumnsState,
    handleFilterChanged: baseFilterChanged,
    handleDeleteFilteredColumn,
    resetFilters,
    handleSortChanged: baseSortChanged,
    handleDeleteSortedColumn,
    handleDeleteSortedAllColumns,
    handleChangeSortColumnState,
  } = useTicketTypeFilters(gridOptions)

  // hasUnsavedChangesをReduxに同期
  useEffect(() => {
    if (hasUnsavedChanges) {
      dispatch(requireSave())
    } else {
      dispatch(doNotRequireSave())
    }
  }, [hasUnsavedChanges, dispatch])

  // リロードボタン押下時の処理
  const onReload = useCallback(() => {
    if (hasUnsavedChanges) {
      // 未保存の変更がある場合は確認ダイアログを表示
      setShowCancelDialog(true)
    } else {
      // 未保存の変更がない場合はそのままリロード
      handleReload()
    }
  }, [hasUnsavedChanges, handleReload])

  // ソート変更時のハンドラー（フック内でドラッグ無効化も処理）
  const onSortChanged = useCallback(
    (event: SortChangedEvent) => {
      baseSortChanged(event)
    },
    [baseSortChanged]
  )

  // フィルター変更時のハンドラー（フック内でドラッグ無効化も処理）
  const onFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      baseFilterChanged(event)
    },
    [baseFilterChanged]
  )

  // 行削除処理
  const handleDeleteRows = useCallback(
    (gridApi: any) => {
      if (!gridApi) return

      const selectedNodes = getSelectedNode(gridApi)
      const selectedUuids = selectedNodes
        .map(node => node.data?.uuid)
        .filter(Boolean)

      if (selectedUuids.length === 0) return

      const newRowData = rowData.filter(
        item => !selectedUuids.includes(item.uuid)
      )
      handleDataChange(newRowData)
    },
    [rowData, handleDataChange]
  )

  // キーボードショートカット用の削除処理（確認ダイアログを開く）
  const handleDeleteRowsByKey = useCallback(() => {
    if (!gridApiRef.current) {
      return
    }

    // 選択されているノードを取得
    const selectedNodes = getSelectedNode(gridApiRef.current)
    if (selectedNodes.length === 0) {
      return
    }

    // プリセット行または使用中の行が選択されている場合は削除しない
    if (selectedNodes.some(node => !isDeletableTicketTypeRow(node.data))) {
      return
    }

    // 削除対象の名前を取得してダイアログを開く
    const targetNames = selectedNodes.map(
      node =>
        node.data?.name || intl.formatMessage({ id: 'displayName.undefined' })
    )
    setDeleteTargetNames(targetNames)
    setShowDeleteConfirmDialog(true)
  }, [])

  // 削除確認ダイアログからの削除実行
  const handleConfirmDelete = useCallback(() => {
    handleDeleteRows(gridApiRef.current)
    setShowDeleteConfirmDialog(false)
    setDeleteTargetNames([])
  }, [handleDeleteRows])

  // 削除確認ダイアログを閉じる
  const handleCloseDeleteDialog = useCallback(() => {
    setShowDeleteConfirmDialog(false)
    setDeleteTargetNames([])
  }, [])

  // キーボードショートカット: Ctrl+S で保存、Alt+Shift+D で削除
  useKeyBind(
    [
      {
        key: KEY_SAVE,
        fn: handleSubmit,
        stopDefaultBehavior: true,
      },
      {
        key: 'alt+shift+d',
        fn: handleDeleteRowsByKey,
      },
    ],
    [handleSubmit, handleDeleteRowsByKey]
  )

  // 行追加・削除のハンドラー
  const handleSetData = useCallback(
    (newData: TicketTypeRow[], _newRows: TicketTypeRow[]) => {
      handleDataChange(newData)
    },
    [handleDataChange]
  )

  // チケットタイプ追加ボタンの処理（複数行追加ダイアログを開く）
  const handleClickAddTicketType = useCallback(() => {
    setAddRowCountDialogState({
      open: true,
      title: intl.formatMessage({
        id: 'ticketType.insert.multipleRow.title',
      }),
      submitHandler: (addRowCount: number) => {
        if (!addRowCount) {
          throw new Error('addRowCount is 0 or undefined.')
        }
        const existingCodes = rowData.map(row => row.code).filter(Boolean)
        const newRows: TicketTypeRow[] = []

        // 指定された数だけ新しい行を作成
        for (let i = 0; i < addRowCount; i++) {
          const allCodes = [...existingCodes, ...newRows.map(row => row.code)]
          const displayOrder = rowData.length + newRows.length
          const newRow = createNewTicketTypeRow(allCodes, displayOrder)
          newRows.push(newRow)
        }

        const newData = [...rowData, ...newRows]
        handleDataChange(newData)
        setAddRowCountDialogState({ open: false })
      },
      closeHandler: () => {
        setAddRowCountDialogState({ open: false })
      },
    })
  }, [rowData, handleDataChange])

  const dragStyle = useDragStyle(gridOptions)

  // ドラッグ&ドロップで並び替え
  const onRowDragEnd = useCallback(
    (event: RowDragEndEvent<TicketTypeRow>) => {
      try {
        if (!event.node.data) return

        const movingData = event.node.data
        const overIndex = event.overIndex

        if (overIndex === undefined || overIndex < 0) return

        // 現在の行データから移動する行を除外
        const newData = rowData.filter(item => item.uuid !== movingData.uuid)
        // 新しい位置に挿入
        newData.splice(overIndex, 0, movingData)

        // displayOrderを更新
        const updatedData = newData.map((item, index) => ({
          ...item,
          displayOrder: index,
        }))

        handleDataChange(updatedData)
      } finally {
        dragStyle.refreshDragStyle()
      }
    },
    [rowData, handleDataChange, dragStyle]
  )

  // コンテキストメニュー
  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams) => {
      return createContextMenuItems(
        params,
        rowData,
        handleSetData,
        () => handleDeleteRows(params.api),
        handleClickAddTicketType
      )
    },
    [rowData, handleSetData, handleDeleteRows, handleClickAddTicketType]
  )

  return (
    <PageArea>
      <SavePopper loading={loading} onSubmit={handleSubmit} />
      <TicketTypesHeader
        loading={loading}
        onSubmit={handleSubmit}
        onReload={onReload}
        onClickAddTicketType={handleClickAddTicketType}
        filteredColumns={filteredColumns}
        onDeleteFilteredColumn={handleDeleteFilteredColumn}
        resetFilters={resetFilters}
        sortColumnsState={sortColumnsState}
        onDeleteSortedColumn={handleDeleteSortedColumn}
        onDeleteSortedAllColumns={handleDeleteSortedAllColumns}
        onChangeSortColumnState={handleChangeSortColumnState}
      />
      <BulkSheetView
        ref={ref}
        gridOptions={gridOptions}
        rowData={rowData}
        onRowDataUpdated={(e: RowDataUpdatedEvent<TicketTypeRow>) => {
          if (e.api) {
            gridApiRef.current = e.api
          }
          e.api?.refreshCells({ columns: ['rowNumber'], force: true })
        }}
        onCellValueChanged={() => {
          const hasChanges = checkForChanges(rowData, originalData)
          if (hasChanges !== hasUnsavedChanges) {
            handleDataChange(rowData)
          }
        }}
        onFilterChanged={onFilterChanged}
        onSortChanged={onSortChanged}
        onRowDragEnter={dragStyle.onRowDragEnter}
        onRowDragMove={dragStyle.onRowDragMove}
        onRowDragEnd={onRowDragEnd}
        onRowDragLeave={dragStyle.refreshDragStyle}
        getContextMenuItems={getContextMenuItems}
        getRowStyle={getRowStyle}
      />
      <Loading isLoading={loading} elem={ref.current} />
      {showCancelDialog && (
        <CancelConfirmDialog
          open={true}
          onConfirm={() => {
            setShowCancelDialog(false)
            handleReload()
          }}
          onClose={() => setShowCancelDialog(false)}
        />
      )}
      <AddRowCountInputDialog {...addRowCountDialogState} />
      {showDeleteConfirmDialog && (
        <DeleteRowConfirmationDialog
          target={deleteTargetNames}
          onConfirm={handleConfirmDelete}
          onClose={handleCloseDeleteDialog}
        />
      )}
    </PageArea>
  )
})

export default connect()(TicketTypes)
